import { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE } from '../../../../ApiUrl';

const TAB_ENDPOINTS = {
  1: `${API_BASE}/professional-profiles/`,
};

// Initial empty state values (import from constants if needed)
const createEmptyState = () => ({
  personalData: {
    legal_full_name: '',
    display_name: '',
    date_of_birth: '',
    gender: '',
    nationalities: [''],
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province: '',
    postal_code: '',
    address_country: 'IN',
    residential_address: '',
    personal_email: '',
    primary_phone: '',
    linkedin_url: '',
  },
  professionalData: {
    headline: '',
    summary: '',
    key_strengths: [],
    primary_role: '',
    additional_roles: [],
    industry_experience: [],
    scope_experience: [],
    qualion_level: '',
    authority_status: '',
  },
  availabilityData: {
    status: '',
    available_from: '',
    notice_period_days: '',
    engagement_types: [],
    preferred_locations: [''],
    offshore_willingness: '',
  },
  commercialData: {
    rate_type: '',
    expected_rate: '',
    currency: '',
  },
});

export const useProfessionalProfile = (professionalProfileId) => {
  const [personalData, setPersonalData] = useState(createEmptyState().personalData);
  const [professionalData, setProfessionalData] = useState(createEmptyState().professionalData);
  const [availabilityData, setAvailabilityData] = useState(createEmptyState().availabilityData);
  const [commercialData, setCommercialData] = useState(createEmptyState().commercialData);

  // ----- Handlers -----
  const handlePersonalChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPersonalData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNationalitiesChange = (e) => {
    const options = e.target.options;
    const values = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) values.push(options[i].value);
    }
    setPersonalData((prev) => ({ ...prev, nationalities: values }));
  };

  const handleProfessionalChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue =
      name === 'key_strengths'
        ? value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : value;
    setProfessionalData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));
  };

  const handleMultiSelectChange = (e, field) => {
    const options = e.target.options;
    const values = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) values.push(options[i].value);
    }
    setProfessionalData((prev) => ({ ...prev, [field]: values }));
  };

  const handleAvailabilityChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAvailabilityData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEngagementTypesChange = (e) => {
    const { value, checked } = e.target;
    setAvailabilityData((prev) => {
      const values = new Set(prev.engagement_types || []);
      if (checked) values.add(value);
      else values.delete(value);
      return { ...prev, engagement_types: Array.from(values) };
    });
  };

  const handleCommercialChange = (e) => {
    const { name, value } = e.target;
    setCommercialData((prev) => ({ ...prev, [name]: value }));
  };

  // ----- Hydration from Profile (for individual profile data) -----
  const hydrateFromProfile = (profile) => {
    if (!profile) return;
    setPersonalData((prev) => ({
      ...prev,
      legal_full_name: profile.legal_full_name ?? prev.legal_full_name,
      display_name: profile.display_name ?? prev.display_name,
      date_of_birth: profile.date_of_birth ?? prev.date_of_birth,
      gender: profile.gender != null ? String(profile.gender) : prev.gender,
      nationalities:
        Array.isArray(profile.nationalities) && profile.nationalities.length
          ? profile.nationalities
          : prev.nationalities,
      address_line_1: profile.address_line_1 ?? prev.address_line_1,
      address_line_2: profile.address_line_2 ?? prev.address_line_2,
      city: profile.city ?? prev.city,
      postal_code: profile.postal_code ?? prev.postal_code,
      address_country:
        profile.country_of_residence ||
        profile.address_country_code ||
        prev.address_country,
      personal_email: profile.personal_email ?? prev.personal_email,
      primary_phone: profile.primary_phone ?? prev.primary_phone,
      linkedin_url: profile.linkedin_url ?? prev.linkedin_url,
    }));

    setProfessionalData((prev) => ({
      ...prev,
      headline: profile.headline ?? prev.headline,
      summary: profile.summary ?? prev.summary,
      key_strengths: Array.isArray(profile.key_strengths)
        ? profile.key_strengths
        : prev.key_strengths,
      primary_role:
        profile.primary_role != null ? String(profile.primary_role) : prev.primary_role,
      additional_roles: Array.isArray(profile.additional_roles)
        ? profile.additional_roles
        : prev.additional_roles,
    }));

    setAvailabilityData((prev) => ({
      ...prev,
      status:
        profile.availability_status != null
          ? String(profile.availability_status)
          : prev.status,
      available_from: profile.available_from ?? prev.available_from,
      notice_period_days:
        profile.notice_period_days === null || profile.notice_period_days === undefined
          ? prev.notice_period_days
          : String(profile.notice_period_days),
      engagement_types: Array.isArray(profile.engagement_types)
        ? profile.engagement_types.map(String)
        : prev.engagement_types,
      preferred_locations:
        Array.isArray(profile.preferred_locations) && profile.preferred_locations.length
          ? profile.preferred_locations
          : prev.preferred_locations,
      offshore_willingness:
        profile.offshore_ready === true
          ? 'Yes'
          : profile.offshore_ready === false
          ? 'No'
          : prev.offshore_willingness,
    }));

    setCommercialData((prev) => ({
      ...prev,
      rate_type:
        profile.rate_type != null ? String(profile.rate_type) : prev.rate_type,
      expected_rate:
        profile.expected_rate === null || profile.expected_rate === undefined
          ? prev.expected_rate
          : String(profile.expected_rate),
      currency: profile.rate_currency || prev.currency,
    }));
  };

  // ----- NEW: Hydrate from Related Records API response -----
// Add this method after hydrateFromProfile
const hydrateFromRelatedRecords = (data) => {
  if (!data) return;
  
  // If the response has a profile object directly
  if (data.profile) {
    hydrateFromProfile(data.profile);
    return;
  }
  
  // If the response has professional_profile data nested differently
  if (data.professional_profile) {
    hydrateFromProfile(data.professional_profile);
    return;
  }
  
  // If the response contains the profile data at the root level (flattened)
  // Check for fields that indicate this is profile data
  if (data.legal_full_name || data.display_name || data.headline) {
    hydrateFromProfile(data);
    return;
  }
};

  // ----- Build payload -----
  const buildTab1ProfilePayload = () => ({
    legal_full_name: personalData.legal_full_name,
    display_name: personalData.display_name,
    date_of_birth: personalData.date_of_birth || null,
    gender: personalData.gender || null,
    nationalities: personalData.nationalities.filter((n) => n.trim()),
    address_line_1: personalData.address_line_1,
    address_line_2: personalData.address_line_2,
    city: personalData.city,
    postal_code: personalData.postal_code,
    country_of_residence: personalData.address_country,
    address_country_code: personalData.address_country,
    personal_email: personalData.personal_email,
    primary_phone: personalData.primary_phone,
    linkedin_url: personalData.linkedin_url,
    headline: professionalData.headline,
    summary: professionalData.summary,
    key_strengths: professionalData.key_strengths,
    primary_role: professionalData.primary_role || null,
    availability_status: availabilityData.status || null,
    available_from: availabilityData.available_from || null,
    notice_period_days:
      availabilityData.notice_period_days === ''
        ? null
        : Number(availabilityData.notice_period_days),
    engagement_types: availabilityData.engagement_types,
    preferred_locations: availabilityData.preferred_locations.filter((loc) => loc.trim()),
    offshore_ready: availabilityData.offshore_willingness === 'Yes',
    rate_type: commercialData.rate_type || null,
    expected_rate:
      commercialData.expected_rate === ''
        ? null
        : Number(commercialData.expected_rate),
    rate_currency: commercialData.currency,
  });

  // ----- Validation -----
  const validate = () => {
    const errors = {};
    // Personal Identity
    if (!personalData.legal_full_name.trim()) errors.legal_full_name = 'Legal full name is required';
    if (!personalData.display_name.trim()) errors.display_name = 'Display name is required';
    if (!personalData.personal_email.trim()) {
      errors.personal_email = 'Personal email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalData.personal_email)) {
      errors.personal_email = 'Please enter a valid email address';
    }
    if (!personalData.primary_phone.trim()) errors.primary_phone = 'Primary phone is required';
    if (!personalData.nationalities.some((n) => n.trim())) {
      errors.nationalities = 'At least one nationality is required';
    }
    // Professional Summary
    if (!professionalData.headline.trim()) errors.headline = 'Professional headline is required';
    if (!professionalData.summary.trim()) errors.summary = 'Professional summary is required';
    if (!professionalData.primary_role.trim()) errors.primary_role = 'Primary role is required';
    // Availability
    if (!availabilityData.status) errors.avail_status = 'Availability status is required';
    if (availabilityData.engagement_types.length === 0) {
      errors.avail_engagement_types = 'At least one engagement type is required';
    }
    return errors;
  };

  // ----- Submit -----
  const submit = async () => {
    if (!professionalProfileId) {
      throw new Error('Professional profile ID missing. Please reload.');
    }
    const payload = buildTab1ProfilePayload();
    await axios.put(`${API_BASE}/professional-profiles/${professionalProfileId}/`, payload);
  };

  return {
    // State
    personalData,
    professionalData,
    availabilityData,
    commercialData,
    // Setters (if needed for nested updates)
    setPersonalData,
    setProfessionalData,
    setAvailabilityData,
    setCommercialData,
    // Handlers
    handlePersonalChange,
    handleNationalitiesChange,
    handleProfessionalChange,
    handleMultiSelectChange,
    handleAvailabilityChange,
    handleEngagementTypesChange,
    handleCommercialChange,
    // Hydration
    hydrateFromProfile,
    hydrateFromRelatedRecords, // NEW: Added this method
    // Validation
    validate,
    // Submit
    submit,
  };
};